import { useMemo, useEffect, useState } from 'react'
import { Container, useWindowSize } from '@moneymade/moneymade-ui'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import LazyLoad from 'react-lazyload'

import { findBanner } from 'utils/checkers'
import { sortByPropDesc } from 'modules/learn/learnUtils'
import LearnTabs from 'modules/learn/LearnTabs/LearnTabs'
import LearnCard from 'modules/learn/LearnCard/LearnCard'
import HeaderBanner from 'components/banners/HeaderBanner/HeaderBanner'
import MobBanner from 'components/banners/MobBanner/MobBanner'
import PrimaryArticle from 'modules/learn/ArticlesPage/PrimaryArticle/PrimaryArticle'
import useTracker from 'hooks/useTracker'
import styles from './ArticlesPage.module.scss'

const NotFound = dynamic(() => import('components/ui/NotFound/NotFound'))

const ArticlesPage = ({ articles, banners, articlesQA }) => {
  const { screenWidth } = useWindowSize()
  const { handleUseTracker } = useTracker()

  // sort Articles by published
  const allArticles = useMemo(
    () => [...articles, ...articlesQA].sort(sortByPropDesc('published')),
    [articles, articlesQA]
  )

  // state for input
  const [search, setSearch] = useState('')
  // state for primaryArticle
  const [primaryArticle, setPrimaryArticle] = useState(allArticles[0] || [])
  // state for otherArticles
  const [otherArticles, setOtherArticles] = useState(allArticles?.slice(1) || [])
  // state for Banner
  const [banner, setBanner] = useState({ header: {}, inText: {}, rightRail: {}, platformBanner: {} })

  useEffect(() => {
    // Find banner for this Page
    const foundBanner = findBanner(banners, 'article')
    if (foundBanner) {
      setBanner(foundBanner)
      if (Object.keys(foundBanner?.header).length) handleUseTracker('BannerInView', 'header', '/learn/article')
    }
  }, [banners, handleUseTracker])

  useEffect(() => {
    if (!document.querySelector('[src="https://moneymade.activehosted.com/f/embed.php?id=1"]')) {
      const script = document.createElement('script')

      script.src = 'https://moneymade.activehosted.com/f/embed.php?id=1'
      script.type = 'text/javascript'
      script.charSet = 'utf-8'

      document.head.appendChild(script)

      return () => {
        document.head.removeChild(script)
      }
    }
  }, [])

  return (
    <>
      {Object.keys(banner?.header).length ? (
        screenWidth > 695 ? (
          <HeaderBanner {...banner.header} />
        ) : (
          <MobBanner {...banner.header} header />
        )
      ) : null}

      <Container width={1200} className={styles.ArticlesPage}>
        <LearnTabs
          activeTab="1"
          search={search}
          handleSetSearch={setSearch}
          allLearnData={allArticles}
          handleSetPrimaryLearn={setPrimaryArticle}
          handleSetOtherLearn={setOtherArticles}
        />

        {!search && <PrimaryArticle {...primaryArticle} />}

        {otherArticles?.length ? (
          <div className={styles.Articles}>
            {otherArticles.map((cardData, index) =>
              // We sho first 15 without lazyloading for SEO
              index < 15 ? (
                <LearnCard once key={index} {...cardData} />
              ) : (
                <LazyLoad key={index} height={200} offset={[-200, 0]} once>
                  <LearnCard once key={index} {...cardData} />
                </LazyLoad>
              )
            )}
          </div>
        ) : (
          <NotFound text="Articles not found" />
        )}
      </Container>
    </>
  )
}

ArticlesPage.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.object),
  articlesQA: PropTypes.arrayOf(PropTypes.object),
  banners: PropTypes.arrayOf(PropTypes.object)
}

ArticlesPage.defaultProps = {
  articles: [],
  articlesQA: [],
  banners: []
}

export default ArticlesPage
